import { ApolloClient, useApolloClient } from '@apollo/client';
import { Alert, Button, DateSelect, MultiSelect, Select } from '@sixfold/common-ui';
import { DateTime } from 'luxon';
import React from 'react';

import { kafkaMessagesQuery } from './graphql';
import { SORT_ORDER, useKafkaContext } from './kafka_context';
import { MessageData } from './messages';
import { Company } from '../../../company';
import { Tour } from '../../../tour/entities';

import styles from '../css/bigquery.module.css';

export interface BigQueryMessage {
  produced_at: string;
  message: string;
  message_type: string;
}

interface Props {
  entity: Tour | Company;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setCompare: React.Dispatch<React.SetStateAction<number[]>>;
  messageOptions: MessageData[];
}

export const KafkaQueryInput: React.FunctionComponent<Props> = (props: Props) => {
  const {
    messageSelection,
    setMessageSelection,
    payloads,
    setPayloads,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    sortOrder,
    setSortOrder,
  } = useKafkaContext();
  const { entity, loading, setLoading, setCompare, messageOptions } = props;
  const client = useApolloClient();

  const tzAbbreviation = DateTime.now().toFormat('ZZZZ');

  const sortOptions = [
    { label: 'asc', value: 'asc' },
    { label: 'desc', value: 'desc' },
  ];

  const messageOptionsMap = new Map<string, MessageData>();
  messageOptions.forEach((messageOption) => {
    messageOptionsMap.set(messageOption.messageType, { ...messageOption });
  });

  const runQuery = async (apolloClient: ApolloClient<object>) => {
    try {
      setLoading(true);
      setCompare([]);
      setPayloads([]);

      const keys = [];
      const messageTypes = [];
      const topics = [];

      for (const selectedMessage of messageSelection) {
        const message = messageOptionsMap.get(selectedMessage);
        if (message) {
          keys.push(message.keyFn(entity));
          messageTypes.push(message.messageType);
          topics.push(message.topic);
        }
      }

      const unique = (arr: string[]) => arr.sort().filter((el, pos, array) => !pos || el !== array[pos - 1]);

      const result = await apolloClient.query({
        query: kafkaMessagesQuery,
        variables: {
          keys: unique(keys),
          messageTypes,
          from: fromDate.toISO(),
          to: toDate.toISO(),
          topics: unique(topics),
        },
      });

      const payloads =
        sortOrder === 'asc' ? [...result.data.bigQueryMessages] : [...result.data.bigQueryMessages].reverse();
      setPayloads(payloads);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div>
        <div className={styles.bigquery__input__field_group}>
          <label>
            <strong>Message type</strong>
          </label>
          <MultiSelect
            label="Message type"
            labelPosition="hidden"
            size="large"
            options={messageOptions.map((messageOption) => ({
              label: messageOption.messageType,
              value: messageOption.messageType,
            }))}
            value={messageSelection.map((ms) => ({ label: ms, value: ms }))}
            onChange={(options) => {
              const values = options.map((o) => o.value);
              setMessageSelection(values);
            }}
          />
        </div>
        <div className={styles.bigquery__input__fields}>
          <div className={styles.bigquery__input__field__from}>
            <DateSelect
              label={`From (start of day, ${tzAbbreviation})`}
              name="fromDate"
              format="dd.MM.yyyy"
              value={fromDate.toJSDate()}
              toDate={DateTime.now().toJSDate()}
              onChange={(date) => {
                if (date !== null) {
                  setFromDate(DateTime.fromJSDate(date).startOf('day'));
                }
              }}
            />
          </div>
          <div className={styles.bigquery__input__field__to}>
            <DateSelect
              label={`To (end of day, ${tzAbbreviation})`}
              name="toDate"
              format="dd.MM.yyyy"
              value={toDate.toJSDate()}
              onChange={(date) => {
                if (date !== null) {
                  setToDate(DateTime.fromJSDate(date).endOf('day'));
                }
              }}
            />
          </div>
        </div>
        {fromDate > toDate && (
          <div style={{ marginBottom: '1em' }}>
            <Alert kind="warning">&apos;From&apos; date must be before &apos;To&apos; date!</Alert>
          </div>
        )}
        {toDate.diff(fromDate, 'months').months > 1 && (
          <div style={{ marginBottom: '1em' }}>
            <Alert kind="warning">Querying for a period longer than 1 month not allowed.</Alert>
          </div>
        )}
      </div>
      <div className={styles.bigquery__input__query__bar}>
        <Button
          kind="primary"
          size="small"
          onClick={() => runQuery(client)}
          loading={loading}
          disabled={
            loading || fromDate > toDate || toDate.diff(fromDate, 'months').months > 1 || messageSelection.length < 1
          }>
          Run query
        </Button>
        <div>
          <Select
            label="Sort order"
            labelPosition="hidden"
            options={sortOptions}
            value={sortOptions.find((o) => o.value === sortOrder)}
            onChange={(option) => {
              if (option) {
                setSortOrder(option.value as SORT_ORDER);
                localStorage.setItem('kafka.sortOrder', option.value);
                const reversedPayloads = [...payloads.reverse()];
                setPayloads(reversedPayloads);
                setCompare([]);
              }
            }}
          />
        </div>
      </div>
    </div>
  );
};
